import { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, BarChart3, Calendar, Download, ExternalLink, FileText, Grid3x3, Layers, Lightbulb, Plus, Search, ShieldCheck, ShieldOff, Store, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmDialog from '@/components/ConfirmDialog';
import {
  TrendingUp, Heart, Package, Sparkles, UserPlus, Activity, MessageSquare
} from 'lucide-react';

// Management panels, lazy-loaded so the analytics landing stays light.
// These were standalone sidebar pages; they now live inside the Analytics
// control center.
const AdminProducts = lazy(() => import('./AdminProducts'));
const AdminUsers = lazy(() => import('./AdminUsers'));
const AdminMerchants = lazy(() => import('./AdminMerchants'));

const MANAGE_PANELS = {
  products: { label: 'Products', Component: AdminProducts },
  users: { label: 'Users', Component: AdminUsers },
  merchants: { label: 'Merchants', Component: AdminMerchants },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '-'; }
};

const todayISO = () => new Date().toISOString();
const daysAgoISO = (d) => new Date(Date.now() - d * 86400 * 1000).toISOString();

function authFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}) }
  });
}

function StatCard({ icon: Icon, label, value, accent, subtext, testId }) {
  return (
    <div data-testid={testId} className="p-5 rounded-xl bg-white shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-bold leading-tight" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>{value}</p>
      <p className="text-xs mt-1" style={{ color: '#73766A' }}>{label}</p>
      {subtext && <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>{subtext}</p>}
    </div>
  );
}

function ManageBlock({ icon: Icon, label, count, sub, accent, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left p-5 rounded-xl bg-white shadow-sm hover:shadow-md transition-all hover:scale-[1.02] flex items-center justify-between gap-3"
      style={{ border: '1px solid #E5E5E5' }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${accent}20`, color: accent }}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>{label}</p>
          <p className="text-xs" style={{ color: '#73766A' }}>{count != null ? `${count} ${sub}` : sub}</p>
        </div>
      </div>
      <span className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap" style={{ background: 'rgba(158,103,82,0.1)', color: '#9E6752' }}>
        Manage →
      </span>
    </button>
  );
}

function FunnelBar({ label, value, total, accent }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-2.5">
      <div className="flex justify-between text-xs mb-1" style={{ color: '#534145' }}>
        <span className="font-medium">{label}</span>
        <span style={{ color: '#73766A' }}>{value} · {pct}%</span>
      </div>
      <div className="h-2.5 rounded-full overflow-hidden" style={{ background: '#F3F4F6' }}>
        <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
      </div>
    </div>
  );
}

function MiniBars({ series, accent = '#9E6752' }) {
  if (!series || series.length === 0) {
    return <p className="text-xs" style={{ color: '#9CA3AF' }}>No data in this range.</p>;
  }
  const max = Math.max(...series.map((s) => s.count || 0), 1);
  return (
    <div className="flex items-end gap-1.5 h-28">
      {series.map((s, i) => (
        <div key={s.date || i} className="flex-1 h-full flex flex-col items-center justify-end" title={`${s.date}: ${s.count}`}>
          <span className="text-[10px] mb-0.5 font-semibold" style={{ color: '#534145' }}>{s.count}</span>
          <div className="w-full rounded-t" style={{ height: `${Math.max((s.count / max) * 100, 5)}%`, background: accent, minWidth: '4px' }} />
          <span className="text-[9px] mt-1" style={{ color: '#9CA3AF' }}>{(s.date || '').slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function EngStat({ label, value }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#FFFAF5' }}>
      <span style={{ color: '#73766A' }}>{label}</span>
      <span className="font-bold" style={{ color: '#20212B' }}>{value}</span>
    </div>
  );
}

function GrowthSection({ growth, fromDate, toDate }) {
  if (!growth) return null;
  const f = growth.funnel;
  const e = growth.engagement;
  return (
    <div data-testid="growth-analytics-section" className="mb-8">
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#20212B' }}>
        <TrendingUp className="w-5 h-5" style={{ color: '#9E6752' }} />
        Growth &amp; Engagement
      </h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard testId="growth-kpi-total-users" icon={Users} label="Total users" value={growth.signups.total} accent="#2D4354" />
        <StatCard testId="growth-kpi-new-signups" icon={UserPlus} label="New signups (range)" value={growth.signups.in_range} accent="#9E6752" subtext={`${fmtDate(fromDate)} – ${fmtDate(toDate)}`} />
        <StatCard testId="growth-kpi-product-views" icon={Activity} label="Product views (range)" value={growth.activity.views} accent="#10B981" />
        <StatCard testId="growth-kpi-avg-responses" icon={MessageSquare} label="Avg responses / quiz" value={e.avg_responses_per_questionnaire} accent="#E8B4B8" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl bg-white shadow-sm" data-testid="growth-funnel">
          <p className="text-sm font-semibold mb-3" style={{ color: '#20212B' }}>Acquisition funnel</p>
          <FunnelBar label="Registered" value={f.registered} total={f.registered} accent="#2D4354" />
          <FunnelBar label="Created a keepsake" value={f.with_recipient} total={f.registered} accent="#9E6752" />
          <FunnelBar label="Created a questionnaire" value={f.with_questionnaire} total={f.registered} accent="#C77D52" />
          <FunnelBar label="Got responses" value={f.questionnaire_with_responses} total={f.registered} accent="#E8B4B8" />
          <FunnelBar label="Analyzed (got AI gifts)" value={f.analyzed} total={f.registered} accent="#10B981" />
        </div>
        <div className="p-5 rounded-xl bg-white shadow-sm" data-testid="growth-signups-trend">
          <p className="text-sm font-semibold mb-3" style={{ color: '#20212B' }}>Signups over time</p>
          <MiniBars series={growth.signups.series} accent="#9E6752" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="p-5 rounded-xl bg-white shadow-sm">
          <p className="text-sm font-semibold mb-3" style={{ color: '#20212B' }}>Engagement totals</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <EngStat label="Keepsakes" value={e.total_recipients} />
            <EngStat label="Wishlists" value={e.total_wishlists} />
            <EngStat label="Questionnaires" value={e.total_questionnaires} />
            <EngStat label="Responses" value={e.total_responses} />
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white shadow-sm" data-testid="growth-sources">
          <p className="text-sm font-semibold mb-3" style={{ color: '#20212B' }}>Top in-app sources</p>
          {(!growth.activity.sources || growth.activity.sources.length === 0) ? (
            <p className="text-xs" style={{ color: '#9CA3AF' }}>No activity in this range.</p>
          ) : growth.activity.sources.slice(0, 6).map((s) => (
            <div key={s.source} className="flex justify-between text-sm py-1.5 border-b" style={{ borderColor: '#F3F4F6' }}>
              <span className="capitalize" style={{ color: '#534145' }}>{s.source}</span>
              <span className="font-semibold" style={{ color: '#9E6752' }}>{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMerchant = searchParams.get('merchant') || '';
  const manageView = searchParams.get('manage') || '';

  const [fromDate, setFromDate] = useState(daysAgoISO(30).slice(0, 10));
  const [toDate, setToDate] = useState(todayISO().slice(0, 10));
  const [summary, setSummary] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [catalog, setCatalog] = useState([]); // full product list for health checks
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showClearAnalytics, setShowClearAnalytics] = useState(false);

  const clearAnalyticsHistory = async () => {
    try {
      const res = await fetch(`${api}/admin/analytics/events`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed to clear');
      toast.success('Analytics history cleared');
      setShowClearAnalytics(false);
      fetchSummary();
      fetchGrowth();
      fetchMerchants();
    } catch (err) {
      toast.error('Failed to clear analytics history');
    }
  };

  // Convert date pickers (YYYY-MM-DD) to full ISO with day boundaries
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (fromDate) p.set('from_date', `${fromDate}T00:00:00Z`);
    if (toDate) p.set('to_date', `${toDate}T23:59:59Z`);
    return p;
  }, [fromDate, toDate]);

  const fetchSummary = useCallback(async () => {
    const res = await authFetch(`${api}/admin/affiliate-analytics/summary?${params.toString()}`);
    if (res.ok) setSummary(await res.json());
  }, [params]);

  const fetchMerchants = useCallback(async () => {
    const res = await authFetch(`${api}/admin/affiliate-analytics/merchants?${params.toString()}`);
    if (res.ok) setMerchants(await res.json());
  }, [params]);

  const fetchGrowth = useCallback(async () => {
    const res = await authFetch(`${api}/admin/growth-analytics/summary?${params.toString()}`);
    if (res.ok) setGrowth(await res.json());
  }, [params]);

  const fetchCatalog = useCallback(async () => {
    const res = await authFetch(`${api}/admin/products`);
    if (res.ok) setCatalog(await res.json());
  }, []);

  const fetchProducts = useCallback(async (merchant) => {
    if (!merchant) return setProducts([]);
    setLoading(true);
    const p2 = new URLSearchParams(params);
    p2.set('merchant', merchant);
    const res = await authFetch(`${api}/admin/affiliate-analytics/products?${p2.toString()}`);
    if (res.ok) {
      const data = await res.json();
      setProducts(data.products || []);
    }
    setLoading(false);
  }, [params]);

  useEffect(() => {
    fetchSummary();
    fetchMerchants();
    fetchGrowth();
  }, [fetchSummary, fetchMerchants, fetchGrowth]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    fetchProducts(selectedMerchant);
  }, [selectedMerchant, fetchProducts]);

  const downloadFile = async (kind /* 'csv' | 'pdf' */) => {
    if (!selectedMerchant) {
      toast.error('Pick a merchant first');
      return;
    }
    const p2 = new URLSearchParams(params);
    p2.set('merchant', selectedMerchant);
    const url = `${api}/admin/affiliate-analytics/export.${kind}?${p2.toString()}`;
    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `mementa-analytics-${selectedMerchant.replace(/\s+/g, '_')}-${fromDate}_${toDate}.${kind}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${kind.toUpperCase()} exported`);
    } catch {
      toast.error(`Failed to export ${kind.toUpperCase()}`);
    }
  };

  // Management view ---------------------------------------------------------
  // The Analytics page doubles as the admin control center: Products, Users
  // and Merchants management render inline here (?manage=products|users|
  // merchants) so admins can act on what the numbers show without leaving
  // the analytics context.
  if (manageView && MANAGE_PANELS[manageView]) {
    const { label, Component } = MANAGE_PANELS[manageView];
    return (
      <div data-testid={`analytics-manage-${manageView}`}>
        <div className="px-4 md:px-8 pt-6">
          <button
            data-testid="manage-back-btn"
            onClick={() => setSearchParams({})}
            className="flex items-center gap-2 text-sm font-medium hover:underline"
            style={{ color: '#9E6752' }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Analytics
          </button>
        </div>
        <Suspense fallback={<div className="p-8 text-sm" style={{ color: '#73766A' }}>Loading {label}…</div>}>
          <Component />
        </Suspense>
      </div>
    );
  }

  // Drilldown view ---------------------------------------------------------
  if (selectedMerchant) {
    const merchantInfo = merchants.find(m => m.name === selectedMerchant);
    const filteredProducts = searchQuery
      ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : products;

    return (
      <div data-testid="admin-analytics-drilldown" className="p-6 sm:p-8 max-w-7xl 2xl:max-w-[1700px] mx-auto">
        <button
          data-testid="analytics-back-btn"
          onClick={() => setSearchParams({})}
          className="flex items-center gap-2 text-sm font-medium mb-4 hover:underline"
          style={{ color: '#9E6752' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to all merchants
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
              {selectedMerchant}
            </h1>
            <p className="text-sm" style={{ color: '#73766A' }}>
              {filteredProducts.length} products · {fmtDate(fromDate)} – {fmtDate(toDate)}
            </p>
            {merchantInfo?.disclosure_required && merchantInfo?.disclosure_text && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs" style={{ background: 'rgba(16,185,129,0.12)', color: '#047857' }}>
                <ShieldCheck className="w-3 h-3" />
                Disclosure: <span className="italic">"{merchantInfo.disclosure_text}"</span>
              </div>
            )}
            {merchantInfo && !merchantInfo.disclosure_required && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs" style={{ background: 'rgba(220,38,38,0.12)', color: '#B91C1C' }}>
                <ShieldOff className="w-3 h-3" />
                Disclosure not required for this merchant
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Calendar className="w-4 h-4" style={{ color: '#73766A' }} />
              <Input data-testid="analytics-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm w-[8.5rem] sm:w-40 font-medium admin-date-input" style={{ color: "#20212B" }} />
              <span className="text-xs" style={{ color: '#73766A' }}>to</span>
              <Input data-testid="analytics-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm w-[8.5rem] sm:w-40 font-medium admin-date-input" style={{ color: "#20212B" }} />
            </div>
            <Button data-testid="analytics-export-csv-btn" onClick={() => downloadFile('csv')} style={{ background: '#9E6752', color: 'white' }}>
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="analytics-export-pdf-btn" onClick={() => downloadFile('pdf')} variant="outline" style={{ borderColor: '#9E6752', color: '#9E6752' }}>
              <FileText className="w-4 h-4 mr-1.5" /> Export PDF
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: '#E5E5E5' }}>
            <Search className="w-4 h-4" style={{ color: '#9CA3AF' }} />
            <input
              data-testid="analytics-product-search"
              placeholder="Filter products by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 outline-none text-sm bg-transparent"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#FFFAF5', borderBottom: '1px solid #E5E5E5' }}>
                <tr>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#534145' }}>Product</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#534145' }}>Category</th>
                  <th className="text-right px-4 py-3 font-semibold" style={{ color: '#534145' }}>Price</th>
                  <th className="text-right px-4 py-3 font-semibold" style={{ color: '#534145' }}>Clicks</th>
                  <th className="text-right px-4 py-3 font-semibold" style={{ color: '#534145' }}>Saves</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#534145' }}>Last click</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#534145' }}>Link</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#73766A' }}>Loading...</td></tr>
                ) : filteredProducts.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#73766A' }}>No products found.</td></tr>
                ) : filteredProducts.map((p) => (
                  <tr key={p.id} data-testid={`analytics-product-row-${p.id}`} className="border-b hover:bg-gray-50" style={{ borderColor: '#F3F4F6' }}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                        <span className="font-medium" style={{ color: '#20212B' }}>{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5" style={{ color: '#73766A' }}>{p.category}</td>
                    <td className="px-4 py-2.5 text-right" style={{ color: '#20212B' }}>${p.price}</td>
                    <td className="px-4 py-2.5 text-right font-semibold" style={{ color: '#9E6752' }}>{p.clicks}</td>
                    <td className="px-4 py-2.5 text-right" style={{ color: '#20212B' }}>{p.saves}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: '#73766A' }}>{fmtDate(p.last_click)}</td>
                    <td className="px-4 py-2.5">
                      {p.merchant_link ? (
                        <a href={p.merchant_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs" style={{ color: '#9E6752' }}>
                          <ExternalLink className="w-3 h-3" /> Open
                        </a>
                      ) : <span className="text-xs" style={{ color: '#9CA3AF' }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Landing view -----------------------------------------------------------
  return (
    <div data-testid="admin-analytics-page" className="p-6 sm:p-8 max-w-7xl 2xl:max-w-[1700px] mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            <BarChart3 className="w-7 h-7" style={{ color: '#9E6752' }} />
            Analytics
          </h1>
          <p className="text-sm" style={{ color: '#73766A' }}>
            Engagement reports plus quick management of products, users and merchants.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="clear-analytics-history-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowClearAnalytics(true)}
            className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Clear history
          </Button>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Calendar className="w-4 h-4" style={{ color: '#73766A' }} />
            <Input data-testid="analytics-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="text-sm w-[8.5rem] sm:w-40 font-medium admin-date-input" style={{ color: "#20212B" }} />
            <span className="text-xs" style={{ color: '#73766A' }}>to</span>
            <Input data-testid="analytics-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="text-sm w-[8.5rem] sm:w-40 font-medium admin-date-input" style={{ color: "#20212B" }} />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showClearAnalytics}
        onOpenChange={setShowClearAnalytics}
        onConfirm={clearAnalyticsHistory}
        title="Clear analytics history?"
        description="This permanently deletes all internal view/click event history (products, decks, categories). Your Google Analytics data is not affected."
        confirmText="Clear History"
      />

      {/* Quick actions (moved from the retired Dashboard tab) */}
      <div data-testid="analytics-quick-actions" className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-6">
        <button
          onClick={() => navigate('/admin/analytics?manage=products', { state: { openCreateModal: true } })}
          className="p-3 rounded-xl text-center transition-all hover:scale-[1.02] cursor-pointer bg-white shadow-sm"
          style={{ border: '1px solid #E5E5E5' }}
          data-testid="quick-action-add-product"
        >
          <Package className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#9E6752' }} />
          <p className="font-semibold text-xs sm:text-sm" style={{ color: '#20212B' }}>Add Product</p>
        </button>
        <button
          onClick={() => navigate('/admin/featured-sections', { state: { openCreateModal: true } })}
          className="p-3 rounded-xl text-center transition-all hover:scale-[1.02] cursor-pointer bg-white shadow-sm"
          style={{ border: '1px solid #E5E5E5' }}
          data-testid="quick-action-create-featured"
        >
          <Layers className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#2D4354' }} />
          <p className="font-semibold text-xs sm:text-sm" style={{ color: '#20212B' }}>Create Featured</p>
        </button>
        <button
          onClick={() => navigate('/admin/categories')}
          className="p-3 rounded-xl text-center transition-all hover:scale-[1.02] cursor-pointer bg-white shadow-sm"
          style={{ border: '1px solid #E5E5E5' }}
          data-testid="quick-action-categories"
        >
          <Grid3x3 className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#73766A' }} />
          <p className="font-semibold text-xs sm:text-sm" style={{ color: '#20212B' }}>Manage Categories</p>
        </button>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="p-3 rounded-xl text-center transition-all hover:scale-[1.02] bg-white shadow-sm"
          style={{ border: '1px solid #E5E5E5' }}
          data-testid="quick-action-preview"
        >
          <ExternalLink className="w-5 h-5 mx-auto mb-1.5" style={{ color: '#9E6752' }} />
          <p className="font-semibold text-xs sm:text-sm" style={{ color: '#20212B' }}>Preview Site</p>
        </a>
      </div>

      {/* KPI row */}
      {summary && (
        <div data-testid="analytics-kpi-row" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard testId="analytics-kpi-clicks" icon={TrendingUp} label="Outbound clicks" value={summary.total_clicks} accent="#9E6752" subtext={`${fmtDate(fromDate)} – ${fmtDate(toDate)}`} />
          <StatCard testId="analytics-kpi-saves" icon={Heart} label="Gift saves" value={summary.total_saves} accent="#E8B4B8" />
          <StatCard testId="analytics-kpi-products" icon={Package} label="Active products" value={summary.total_products} accent="#FED7A5" />
          <StatCard testId="analytics-kpi-top-merchant" icon={Sparkles} label="Top merchant" value={summary.top_merchant || '-'} accent="#10B981" subtext={summary.top_merchant ? `${summary.top_merchant_clicks} clicks` : 'No clicks yet'} />
        </div>
      )}

      {/* Growth & engagement (additive, computed from existing data) */}
      <GrowthSection growth={growth} fromDate={fromDate} toDate={toDate} />

      {/* Control center, inline management blocks for the areas the
          analytics above report on. */}
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: '#20212B' }}>
        <BarChart3 className="w-5 h-5" style={{ color: '#9E6752' }} />
        Control Center
      </h2>
      <div data-testid="control-center-row" className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <ManageBlock
          icon={Package}
          label="Products"
          count={summary?.total_products}
          sub="active products"
          accent="#9E6752"
          onClick={() => setSearchParams({ manage: 'products' })}
          testId="manage-block-products"
        />
        <ManageBlock
          icon={Users}
          label="Users"
          count={summary?.total_users}
          sub="registered users"
          accent="#2D4354"
          onClick={() => setSearchParams({ manage: 'users' })}
          testId="manage-block-users"
        />
        <ManageBlock
          icon={Store}
          label="Merchants"
          count={summary?.merchant_count}
          sub="active merchants"
          accent="#10B981"
          onClick={() => setSearchParams({ manage: 'merchants' })}
          testId="manage-block-merchants"
        />
      </div>

      {/* Content health (moved from the retired Dashboard tab) */}
      <ContentHealthCard catalog={catalog} />

      {/* Merchant tile grid */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#20212B' }}>
          <Store className="w-5 h-5" style={{ color: '#9E6752' }} />
          Merchant Performance
        </h2>
      </div>

      {merchants.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center shadow-sm">
          <Store className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: '#9E6752' }} />
          <p className="font-semibold mb-1" style={{ color: '#20212B' }}>No merchants yet</p>
          <p className="text-sm" style={{ color: '#73766A' }}>Add merchants via the Control Center above to see analytics.</p>
        </div>
      ) : (
        <div data-testid="merchant-tile-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {merchants.map((m) => (
            <button
              key={m.id}
              data-testid={`merchant-tile-${m.id}`}
              onClick={() => setSearchParams({ merchant: m.name })}
              className="text-left p-5 rounded-xl bg-white shadow-sm hover:shadow-md transition-all hover:scale-[1.02]"
              style={{ border: '1px solid #E5E5E5' }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: 'rgba(158, 103, 82, 0.12)', color: '#9E6752' }}>
                  <Store className="w-5 h-5" />
                </div>
                {m.disclosure_required ? (
                  <ShieldCheck className="w-4 h-4" style={{ color: '#10B981' }} title="Disclosure on" />
                ) : (
                  <ShieldOff className="w-4 h-4" style={{ color: '#DC2626' }} title="Disclosure off" />
                )}
              </div>
              <p className="text-base font-semibold" style={{ color: '#20212B', fontFamily: 'Playfair Display, serif' }}>{m.name}</p>
              <p className="text-xs mb-3" style={{ color: '#73766A' }}>{m.product_count} products</p>
              <div className="flex items-center gap-4 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
                <div>
                  <p className="text-lg font-bold" style={{ color: '#9E6752' }}>{m.clicks}</p>
                  <p className="text-[11px]" style={{ color: '#73766A' }}>clicks</p>
                </div>
                <div>
                  <p className="text-lg font-bold" style={{ color: '#E8B4B8' }}>{m.saves}</p>
                  <p className="text-[11px]" style={{ color: '#73766A' }}>saves</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs mt-8 text-center" style={{ color: '#9CA3AF' }}>
        Tracking is privacy-friendly: only outbound merchant clicks are stored, with timestamp + source. No personal browsing data.
      </p>
    </div>
  );
}

function ContentHealthCard({ catalog }) {
  const { issues, suggestions, score } = useMemo(() => {
    const issues = [];
    const suggestions = [];
    if (!catalog.length) return { issues, suggestions, score: null };

    const noDesc = catalog.filter((p) => !p.description || p.description.trim() === '').length;
    if (noDesc > 0) issues.push(`${noDesc} product${noDesc > 1 ? 's' : ''} missing descriptions`);
    const noImage = catalog.filter((p) => !p.image_url || p.image_url.includes('placeholder')).length;
    if (noImage > 0) issues.push(`${noImage} product${noImage > 1 ? 's' : ''} with missing/placeholder images`);

    const categoryCount = {};
    catalog.forEach((p) => { categoryCount[p.category] = (categoryCount[p.category] || 0) + 1; });
    Object.entries(categoryCount).forEach(([cat, count]) => {
      if (count < 5) suggestions.push(`"${cat}" only has ${count} product${count > 1 ? 's' : ''}, consider adding more`);
    });
    const noTags = catalog.filter((p) => !p.tags || p.tags.length === 0).length;
    if (noTags > 5) suggestions.push(`${noTags} products missing tags, improve discoverability`);

    let score = 100;
    score -= (noDesc / catalog.length) * 20;
    score -= (catalog.filter((p) => !p.image_url).length / catalog.length) * 20;
    score -= (noTags / catalog.length) * 30;
    score -= (catalog.filter((p) => !p.is_active).length / catalog.length) * 10;
    return { issues, suggestions, score: Math.max(0, Math.round(score)) };
  }, [catalog]);

  if (score === null) return null;

  return (
    <div data-testid="content-health-card" className="bg-white rounded-xl p-5 shadow-sm mb-8" style={{ border: '1px solid #E5E5E5' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#20212B' }}>
          <ShieldCheck className="w-5 h-5" style={{ color: '#9E6752' }} />
          Content Health
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: '#73766A' }}>Quality score</span>
          <span
            className="text-lg font-bold px-3 py-0.5 rounded-full"
            style={{
              color: score > 80 ? '#065F46' : score > 60 ? '#92400E' : '#991B1B',
              background: score > 80 ? 'rgba(16,185,129,0.12)' : score > 60 ? 'rgba(217,119,6,0.12)' : 'rgba(220,38,38,0.1)'
            }}
            data-testid="content-health-score"
          >
            {score}%
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: '#991B1B' }}>
            <AlertTriangle className="w-3.5 h-3.5" /> Attention needed
          </p>
          {issues.length === 0 ? (
            <p className="text-sm py-3" style={{ color: '#73766A' }}>Everything looks good!</p>
          ) : (
            <ul className="space-y-1.5">
              {issues.map((issue, idx) => (
                <li key={`hc-issue-${idx}`} className="text-sm p-2.5 rounded-lg" style={{ background: '#FEF2F2', color: '#991B1B' }}>
                  {issue}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: '#9E6752' }}>
            <Lightbulb className="w-3.5 h-3.5" /> Suggestions
          </p>
          {suggestions.length === 0 ? (
            <p className="text-sm py-3" style={{ color: '#73766A' }}>You&apos;re all set, keep it up.</p>
          ) : (
            <ul className="space-y-1.5">
              {suggestions.slice(0, 3).map((s, idx) => (
                <li key={`hc-sug-${idx}`} className="text-sm p-2.5 rounded-lg" style={{ background: '#FFF8F0', color: '#534145' }}>
                  {s}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
